/**
 * <s-listing-map> — interactive map of property listings (Leaflet + OpenStreetMap tiles).
 *
 * Reads listings from a child <script type="application/json" data-listing-map-data>:
 *   [{ title, url, price, deal: 'sale'|'rent', type, typeLabel, location, text, lat?, lng?, image? }]
 * Pins use the listing's own lat/lng (product metafields custom.latitude / custom.longitude) when present;
 * otherwise the district/province named in `location` or `text` is matched against DISTRICTS below,
 * with a small deterministic offset so listings in the same district don't stack.
 *
 * Commands (Invoker Commands, commandfor = this element's id):
 *   --deal  (data-value: all | sale | rent)  filters pins
 *   --locate                                  centers the map on the visitor (geolocation)
 *
 * Strings come from data attributes: data-locating, data-geo-error, data-view, data-per-month, data-count.
 */
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

// Approximate centers (lat, lng). Districts first, provinces last so the most specific match wins.
const DISTRICTS = [
  ['อโศก', 13.7411, 100.5606], ['สุขุมวิท', 13.7308, 100.5689], ['ทองหล่อ', 13.7320, 100.5830],
  ['คลองเตย', 13.7130, 100.5580], ['บางนา', 13.6680, 100.6050], ['ลาดพร้าว', 13.8160, 100.5950],
  ['รัชดาภิเษก', 13.7790, 100.5740], ['พระราม 9', 13.7570, 100.5650], ['บางคอแหลม', 13.6930, 100.5030],
  ['บางแสน', 13.2830, 100.9150], ['ศรีราชา', 13.1740, 100.9300], ['พัทยา', 12.9236, 100.8825],
  ['บางละมุง', 13.0470, 100.9290], ['เมืองชลบุรี', 13.3611, 100.9847],
  ['บางพลี', 13.6060, 100.7060], ['เทพารักษ์', 13.6190, 100.6450], ['บางบ่อ', 13.5800, 100.8400],
  ['เมืองสมุทรปราการ', 13.5991, 100.5998],
  ['บ้านฉาง', 12.7230, 101.0640], ['ปลวกแดง', 12.9760, 101.2190], ['แกลง', 12.7790, 101.6510],
  ['เมืองระยอง', 12.6814, 101.2816],
  ['ท่าช้าง', 12.6230, 102.1200], ['ตากสิน', 12.6150, 102.1100], ['ท่าใหม่', 12.6190, 102.0080],
  ['ขลุง', 12.4560, 102.2250], ['เมืองจันทบุรี', 12.6114, 102.1039],
  ['แหลมงอบ', 12.1730, 102.3950], ['เกาะช้าง', 12.0500, 102.3400], ['เมืองตราด', 12.2428, 102.5175],
  ['แก่งคอย', 14.5860, 101.0000], ['มวกเหล็ก', 14.6560, 101.1950], ['เมืองสระบุรี', 14.5289, 100.9101],
  ['บางปะกง', 13.5050, 100.9590], ['บางคล้า', 13.7230, 101.2100], ['เมืองฉะเชิงเทรา', 13.6904, 101.0779],
  ['กรุงเทพ', 13.7563, 100.5018], ['ชลบุรี', 13.3611, 100.9847], ['สมุทรปราการ', 13.5991, 100.5998],
  ['ระยอง', 12.6814, 101.2816], ['จันทบุรี', 12.6114, 102.1039], ['ตราด', 12.2428, 102.5175],
  ['สระบุรี', 14.5289, 100.9101], ['ฉะเชิงเทรา', 13.6904, 101.0779],
];
const THAILAND_CENTER = [13.2, 101.0];

// Pin and popup styles live here because Leaflet builds this markup at runtime (outside the Tailwind scan).
const PIN_CSS = `
.gt-pin {
  background: none;
  border: 0;
}
.gt-pin__label {
  position: absolute;
  inset-inline-start: 0;
  top: 0;
  transform: translate(-50%, calc(-100% - 8px));
  white-space: nowrap;
  border-radius: 9999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  color: var(--color-surface);
  box-shadow: 0 4px 12px rgb(14 35 64 / 0.3);
  border: 2px solid var(--color-surface);
}
[dir='rtl'] .gt-pin__label {
  transform: translate(50%, calc(-100% - 8px));
}
.gt-pin__label::after {
  content: '';
  position: absolute;
  inset-inline-start: 50%;
  bottom: -7px;
  width: 10px;
  height: 10px;
  margin-inline-start: -5px;
  background: inherit;
  border-inline-end: 2px solid var(--color-surface);
  border-bottom: 2px solid var(--color-surface);
  transform: rotate(45deg);
}
.gt-pin__label--sale {
  background: var(--color-on-surface);
}
.gt-pin__label--rent {
  background: var(--color-accent);
}
.gt-pin__card {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: var(--color-on-surface) !important;
  text-decoration: none !important;
  font-family: var(--font-body);
}
.gt-pin__img {
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 10px;
  margin-bottom: 4px;
}
.gt-pin__badge {
  align-self: flex-start;
  border-radius: 9999px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  color: var(--color-surface);
}
.gt-pin__badge--sale {
  background: var(--color-on-surface);
}
.gt-pin__badge--rent {
  background: var(--color-accent);
}
.gt-pin__title {
  font-size: 14px;
  line-height: 1.35;
}
.gt-pin__loc {
  font-size: 12px;
  color: var(--color-on-surface-secondary);
}
.gt-pin__price {
  font-size: 15px;
  font-weight: 700;
  color: var(--color-interactive);
}
.gt-pin__view {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-accent);
}
.gt-pin__coords {
  font-size: 13px;
  background: var(--color-surface-secondary);
  border-radius: 8px;
  padding: 4px 8px;
  user-select: all;
}
.gt-pin__copy {
  margin-top: 4px;
  min-height: 36px;
  border-radius: 9999px;
  background: var(--color-interactive);
  color: var(--color-interactive-text, #fff);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.leaflet-marker-draggable .gt-pin__label {
  cursor: grab;
}
s-listing-map .leaflet-popup-content-wrapper {
  border-radius: 16px;
}
s-listing-map .leaflet-container {
  font-family: var(--font-body);
}
`;
function injectPinStyles() {
  if (document.getElementById('gt-pin-styles')) return;
  const style = document.createElement('style');
  style.id = 'gt-pin-styles';
  style.textContent = PIN_CSS;
  document.head.append(style);
}

let leafletPromise;
function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  leafletPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => resolve(window.L);
    script.onerror = reject;
    document.head.append(script);
  });
  return leafletPromise;
}

function geocode(listing, index) {
  const lat = Number(listing.lat);
  const lng = Number(listing.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0) return [lat, lng];
  const haystack = `${listing.location || ''} ${listing.text || ''}`;
  const match = DISTRICTS.find(([name]) => haystack.includes(name));
  if (!match) return null;
  // Golden-angle spiral keeps same-district pins apart without randomness.
  const angle = index * 2.39996;
  const radius = 0.004 + (index % 7) * 0.0018;
  return [match[1] + Math.sin(angle) * radius, match[2] + Math.cos(angle) * radius];
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

class SListingMap extends HTMLElement {
  #ac;
  #map;
  #layers = { sale: null, rent: null };
  #markers = [];
  #deal = 'all';
  #youMarker;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener(
      'command',
      (event) => {
        if (event.command === '--deal') {
          this.#deal = (event.source instanceof HTMLElement && event.source.dataset.value) || 'all';
          this.querySelectorAll('[command="--deal"]').forEach((button) => {
            button.setAttribute('aria-pressed', String((button.dataset.value || 'all') === this.#deal));
          });
          this.#applyFilter();
        } else if (event.command === '--locate') {
          this.#locate();
        }
      },
      { signal: this.#ac.signal }
    );

    const canvas = this.querySelector('[data-map-canvas]');
    if (!canvas) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        this.#init(canvas);
      },
      { rootMargin: '200px' }
    );
    observer.observe(canvas);
    this.#ac.signal.addEventListener('abort', () => observer.disconnect());
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#map?.remove();
    this.#map = null;
  }

  async #init(canvas) {
    let L;
    try {
      L = await loadLeaflet();
    } catch {
      this.#status(this.dataset.geoError);
      return;
    }
    if (!this.isConnected) return;
    injectPinStyles();

    let listings = [];
    try {
      listings = JSON.parse(this.querySelector('[data-listing-map-data]')?.textContent || '[]');
    } catch {
      listings = [];
    }

    this.#map = L.map(canvas, { scrollWheelZoom: false, zoomControl: true }).setView(THAILAND_CENTER, 7);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(this.#map);
    this.#map.on('focus', () => this.#map.scrollWheelZoom.enable());
    this.#map.on('blur', () => this.#map.scrollWheelZoom.disable());

    this.#layers.sale = L.featureGroup().addTo(this.#map);
    this.#layers.rent = L.featureGroup().addTo(this.#map);

    const perMonth = this.dataset.perMonth || '';
    const viewLabel = this.dataset.view || '';
    listings.forEach((listing, index) => {
      const point = geocode(listing, index);
      if (!point) return;
      const deal = listing.deal === 'rent' ? 'rent' : 'sale';
      const icon = L.divIcon({
        className: 'gt-pin',
        html: `<span class="gt-pin__label gt-pin__label--${deal}">${escapeHtml(listing.priceShort || listing.price)}</span>`,
        iconSize: null,
        iconAnchor: [0, 0],
      });
      const image = listing.image ? `<img src="${escapeHtml(listing.image)}" alt="" class="gt-pin__img" loading="lazy">` : '';
      const popup = `
        <a href="${escapeHtml(listing.url)}" class="gt-pin__card">
          ${image}
          <span class="gt-pin__badge gt-pin__badge--${deal}">${escapeHtml(listing.dealLabel)} · ${escapeHtml(listing.typeLabel)}</span>
          <strong class="gt-pin__title">${escapeHtml(listing.title)}</strong>
          <span class="gt-pin__loc">${escapeHtml(listing.location)}</span>
          <span class="gt-pin__price">${escapeHtml(listing.price)}${deal === 'rent' ? ` ${escapeHtml(perMonth)}` : ''}</span>
          <span class="gt-pin__view">${escapeHtml(viewLabel)} →</span>
        </a>`;
      const editable = this.hasAttribute('data-editable');
      const marker = L.marker(point, { icon, title: listing.title, keyboard: true, draggable: editable }).bindPopup(popup, { maxWidth: 260, minWidth: 220 });
      if (editable) marker.on('dragend', () => this.#showPosition(marker, listing));
      marker.addTo(this.#layers[deal]);
      this.#markers.push({ marker, deal });
    });

    this.#applyFilter();
  }

  // Editor-only: after a drag, show the new coordinates to copy into the listing's
  // ละติจูด / ลองจิจูด fields (custom.latitude / custom.longitude). The storefront can't save them itself.
  #showPosition(marker, listing) {
    const { lat, lng } = marker.getLatLng();
    const latText = lat.toFixed(6);
    const lngText = lng.toFixed(6);
    const admin = listing.adminUrl
      ? `<a href="${escapeHtml(listing.adminUrl)}" target="_blank" rel="noopener" class="gt-pin__view">${escapeHtml(this.dataset.openAdmin)} ↗</a>`
      : '';
    const html = `
      <div class="gt-pin__card">
        <strong class="gt-pin__title">${escapeHtml(listing.title)}</strong>
        <span class="gt-pin__loc">${escapeHtml(this.dataset.newPosition)}</span>
        <code class="gt-pin__coords">${latText}, ${lngText}</code>
        <span class="gt-pin__loc">Latitude: <b>${latText}</b><br>Longitude: <b>${lngText}</b></span>
        <button type="button" class="gt-pin__copy" data-copy-coords="${latText}, ${lngText}">${escapeHtml(this.dataset.copy)}</button>
        ${admin}
      </div>`;
    marker.setPopupContent(html).openPopup();
    const button = marker.getPopup()?.getElement()?.querySelector('[data-copy-coords]');
    button?.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(button.dataset.copyCoords);
        button.textContent = this.dataset.copied || '✓';
      } catch {
        button.textContent = button.dataset.copyCoords;
      }
    });
  }

  #applyFilter() {
    if (!this.#map) return;
    for (const deal of ['sale', 'rent']) {
      const layer = this.#layers[deal];
      const show = this.#deal === 'all' || this.#deal === deal;
      if (show && !this.#map.hasLayer(layer)) layer.addTo(this.#map);
      if (!show && this.#map.hasLayer(layer)) this.#map.removeLayer(layer);
    }
    const visible = this.#markers.filter(({ deal }) => this.#deal === 'all' || this.#deal === deal);
    const count = this.querySelector('[data-map-count]');
    if (count) count.textContent = (this.dataset.count || '{{ count }}').replace('{{ count }}', String(visible.length));
    if (visible.length > 0) {
      const bounds = window.L.latLngBounds(visible.map(({ marker }) => marker.getLatLng()));
      this.#map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
    }
  }

  #locate() {
    if (!this.#map || !('geolocation' in navigator)) {
      this.#status(this.dataset.geoError);
      return;
    }
    this.#status(this.dataset.locating);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const here = [coords.latitude, coords.longitude];
        this.#youMarker?.remove();
        this.#youMarker = window.L.circleMarker(here, {
          radius: 9,
          color: '#ffffff',
          weight: 3,
          fillColor: '#0B4F8A',
          fillOpacity: 1,
        }).addTo(this.#map);
        this.#map.setView(here, 12);
        this.#status('');
      },
      () => this.#status(this.dataset.geoError),
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }

  #status(message) {
    const status = this.querySelector('[data-map-status]');
    if (!status) return;
    status.textContent = message || '';
    status.hidden = !message;
  }
}

if (!customElements.get('s-listing-map')) customElements.define('s-listing-map', SListingMap);
